/**
 * Streams Context - Global state management for streams
 *
 * Provides streams data and CRUD operations to all components.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { api, ApiError } from '../lib/api';
import { axiom } from '../lib/axiom';
import { useAuth } from './AuthContext';
import { autoLayout } from '../lib/graphLayout';
import type { Stream, StreamWithGraph, Node, Edge, CreateStreamInput, UpdateStreamInput, NodeStatus, NodeMeta, StreamEvent } from '../types/api';

interface StreamsContextType {
  // State
  streams: Stream[];
  loading: boolean;
  error: string | null;
  selectedStreamId: string | null;
  currentStreamData: StreamWithGraph | null;
  currentStreamLoading: boolean;

  // Stream operations
  fetchStreams: () => Promise<void>;
  retryFetchStreams: () => Promise<void>;
  createStream: (data: CreateStreamInput) => Promise<Stream>;
  updateStream: (id: string, data: UpdateStreamInput) => Promise<void>;
  deleteStream: (id: string) => Promise<void>;
  selectStream: (id: string | null) => void;
  fetchStreamData: (id: string) => Promise<StreamWithGraph>;

  // Node operations
  createNode: (streamId: string, name: string, x: number, y: number, status?: NodeStatus, meta?: Record<string, unknown>) => Promise<Node>;
  updateNode: (id: string, data: Partial<{ name: string; status: NodeStatus; x: number; y: number; meta: NodeMeta }>) => Promise<void>;
  deleteNode: (id: string) => Promise<void>;

  // Edge operations
  createEdge: (streamId: string, fromNodeId: string, toNodeId: string, label?: string) => Promise<Edge>;
  deleteEdge: (id: number) => Promise<void>;

  // Refresh current stream
  refreshCurrentStream: () => Promise<void>;

  // Auto layout
  autoLayoutStream: (viewportWidth: number, viewportHeight: number) => Promise<void>;

  // WebSocket event handler
  handleWebSocketEvent: (event: StreamEvent) => void;
}

const StreamsContext = createContext<StreamsContextType | null>(null);

export function StreamsProvider({ children }: { children: React.ReactNode }) {
  const { user, accessInfo } = useAuth();

  const [streams, setStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStreamId, setSelectedStreamId] = useState<string | null>(null);
  const [currentStreamData, setCurrentStreamData] = useState<StreamWithGraph | null>(null);
  const [currentStreamLoading, setCurrentStreamLoading] = useState(false);

  // Fetch all streams
  const fetchStreams = useCallback(async () => {
    if (!user || !accessInfo?.hasAccess) return;

    setLoading(true);
    setError(null);

    try {
      const data = await api.streams.list();
      setStreams(data);

      // Auto-select first stream if none selected
      if (!selectedStreamId && data.length > 0) {
        setSelectedStreamId(data[0].id);
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load streams';
      setError(message);
      axiom.error('Failed to fetch streams', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setLoading(false);
    }
  }, [user, accessInfo?.hasAccess, selectedStreamId]);

  // Retry fetching streams after a failure
  const retryFetchStreams = useCallback(async () => {
    setError(null);
    await fetchStreams();
  }, [fetchStreams]);

  // Fetch stream with full graph data
  const fetchStreamData = useCallback(async (id: string): Promise<StreamWithGraph> => {
    setCurrentStreamLoading(true);
    try {
      const data = await api.streams.get(id);
      setCurrentStreamData(data);
      return data;
    } catch (err) {
      axiom.error('Failed to fetch stream data', { error: err instanceof Error ? err.message : String(err) });
      throw err;
    } finally {
      setCurrentStreamLoading(false);
    }
  }, []);

  // Create new stream
  const createStream = useCallback(async (data: CreateStreamInput): Promise<Stream> => {
    const newStream = await api.streams.create(data);
    setStreams((prev) => [newStream, ...prev]);
    setSelectedStreamId(newStream.id);
    return newStream;
  }, []);

  // Update stream
  const updateStream = useCallback(async (id: string, data: UpdateStreamInput): Promise<void> => {
    await api.streams.update(id, data);
    setStreams((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...data, updated_at: new Date().toISOString() } : s))
    );
  }, []);

  // Delete stream
  const deleteStream = useCallback(async (id: string): Promise<void> => {
    await api.streams.delete(id);
    setStreams((prev) => prev.filter((s) => s.id !== id));

    // Select another stream if current was deleted
    if (selectedStreamId === id) {
      setStreams((prev) => {
        const remaining = prev.filter((s) => s.id !== id);
        setSelectedStreamId(remaining[0]?.id || null);
        return remaining;
      });
    }
  }, [selectedStreamId]);

  // Select stream
  const selectStream = useCallback((id: string | null) => {
    setSelectedStreamId(id);

    // Set Sentry tag for active stream context
    if (id) {
      Sentry.setTag('active_stream', id);
      fetchStreamData(id).catch((err) => axiom.error('Error caught', { error: err instanceof Error ? err.message : String(err) }));
    } else {
      // Clear tag by setting empty string (undefined is not valid)
      Sentry.setTag('active_stream', '');
      setCurrentStreamData(null);
    }
  }, [fetchStreamData]);

  // Refresh current stream data
  const refreshCurrentStream = useCallback(async () => {
    if (selectedStreamId) {
      await fetchStreamData(selectedStreamId);
      // Also update the streams list
      await fetchStreams();
    }
  }, [selectedStreamId, fetchStreamData, fetchStreams]);

  // Node operations
  const createNode = useCallback(async (streamId: string, name: string, x: number, y: number, status?: NodeStatus, meta?: Record<string, unknown>): Promise<Node> => {
    const node = await api.nodes.create(streamId, { name, x, y, status, meta: meta as NodeMeta });

    // Update local state
    if (currentStreamData && currentStreamData.id === streamId) {
      setCurrentStreamData((prev) =>
        prev ? { ...prev, nodes: [...prev.nodes, node] } : prev
      );
    }

    // Update stream progress
    setStreams((prev) =>
      prev.map((s) =>
        s.id === streamId
          ? { ...s, progress: { ...s.progress, total: s.progress.total + 1 } }
          : s
      )
    );

    return node;
  }, [currentStreamData]);

  const updateNode = useCallback(async (id: string, data: Partial<{ name: string; status: NodeStatus; x: number; y: number; meta: NodeMeta }>): Promise<void> => {
    await api.nodes.update(id, data);

    // Update local state - handle meta specially (it's stored as JSON string in the node)
    if (currentStreamData) {
      setCurrentStreamData((prev) =>
        prev
          ? {
              ...prev,
              nodes: prev.nodes.map((n): Node => {
                if (n.id !== id) return n;

                // Build the update, excluding meta initially (destructured to avoid passing to spread)
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { meta: metaFromData, ...otherData } = data;
                let updatedMeta = n.meta;

                // If updating meta, merge with existing and stringify
                if (data.meta) {
                  let existingMeta = {};
                  if (n.meta) {
                    try {
                      existingMeta = typeof n.meta === 'string' ? JSON.parse(n.meta) : n.meta;
                    } catch {
                      axiom.warn('Failed to parse existing meta for node', { node_id: n.id, meta: n.meta });
                    }
                  }
                  updatedMeta = JSON.stringify({ ...existingMeta, ...data.meta });
                }

                return {
                  ...n,
                  ...otherData,
                  meta: updatedMeta,
                  updated_at: new Date().toISOString(),
                };
              }),
            }
          : prev
      );

      // Update progress if status changed to completed
      if (data.status === 'completed') {
        const node = currentStreamData.nodes.find((n) => n.id === id);
        if (node && node.status !== 'completed') {
          setStreams((prev) =>
            prev.map((s) =>
              s.id === currentStreamData.id
                ? { ...s, progress: { ...s.progress, completed: s.progress.completed + 1 } }
                : s
            )
          );
        }
      }
    }
  }, [currentStreamData]);

  const deleteNode = useCallback(async (id: string): Promise<void> => {
    // Find the node before deleting
    const node = currentStreamData?.nodes.find((n) => n.id === id);

    await api.nodes.delete(id);

    // Update local state
    if (currentStreamData) {
      setCurrentStreamData((prev) =>
        prev
          ? {
              ...prev,
              nodes: prev.nodes.filter((n) => n.id !== id),
              edges: prev.edges.filter((e) => e.from_node_id !== id && e.to_node_id !== id),
            }
          : prev
      );

      // Update stream progress
      setStreams((prev) =>
        prev.map((s) =>
          s.id === currentStreamData.id
            ? {
                ...s,
                progress: {
                  completed: node?.status === 'completed' ? s.progress.completed - 1 : s.progress.completed,
                  total: s.progress.total - 1,
                },
              }
            : s
        )
      );
    }
  }, [currentStreamData]);

  // Edge operations
  const createEdge = useCallback(async (streamId: string, fromNodeId: string, toNodeId: string, label?: string): Promise<Edge> => {
    const edge = await api.edges.create(streamId, {
      from_node_id: fromNodeId,
      to_node_id: toNodeId,
      label,
    });

    // Update local state
    if (currentStreamData && currentStreamData.id === streamId) {
      setCurrentStreamData((prev) =>
        prev ? { ...prev, edges: [...prev.edges, edge] } : prev
      );
    }

    return edge;
  }, [currentStreamData]);

  const deleteEdge = useCallback(async (id: number): Promise<void> => {
    await api.edges.delete(id);

    // Update local state
    if (currentStreamData) {
      setCurrentStreamData((prev) =>
        prev ? { ...prev, edges: prev.edges.filter((e) => e.id !== id) } : prev
      );
    }
  }, [currentStreamData]);

  // Auto layout - reorganize nodes to fit viewport
  const autoLayoutStream = useCallback(async (
    viewportWidth: number,
    viewportHeight: number
  ): Promise<void> => {
    if (!currentStreamData || currentStreamData.nodes.length === 0) return;

    // Calculate new positions using Dagre
    const newPositions = autoLayout(
      currentStreamData.nodes,
      currentStreamData.edges,
      viewportWidth,
      viewportHeight,
      { direction: 'LR' }
    );

    // Update all node positions in database
    await Promise.all(
      newPositions.map(pos =>
        api.nodes.update(pos.id, { x: pos.x, y: pos.y })
      )
    );

    // Update local state
    setCurrentStreamData(prev => prev ? {
      ...prev,
      nodes: prev.nodes.map(node => {
        const newPos = newPositions.find(p => p.id === node.id);
        return newPos ? { ...node, x: newPos.x, y: newPos.y } : node;
      }),
    } : prev);
  }, [currentStreamData]);

  // Handle WebSocket events for real-time state sync
  const handleWebSocketEvent = useCallback((event: StreamEvent) => {
    if (!currentStreamData) return;

    switch (event.type) {
      case 'node_created': {
        if (event.node.stream_id === currentStreamData.id) {
          setCurrentStreamData((prev) =>
            prev ? {
              ...prev,
              nodes: [...prev.nodes, {
                id: event.node.id,
                stream_id: event.node.stream_id,
                name: event.node.name,
                status: event.node.status,
                x: event.node.x,
                y: event.node.y,
                meta: event.node.meta ? JSON.stringify(event.node.meta) : null,
                created_at: event.timestamp,
                updated_at: event.timestamp,
              }],
            } : prev
          );
          setStreams((prev) =>
            prev.map((s) =>
              s.id === event.node.stream_id
                ? { ...s, progress: { ...s.progress, total: s.progress.total + 1 } }
                : s
            )
          );
        }
        break;
      }

      case 'node_updated': {
        setCurrentStreamData((prev) => {
          if (!prev) return prev;
          const node = prev.nodes.find((n) => n.id === event.node_id);
          if (!node) return prev;

          // Check for completed status transitions (symmetric progress update)
          if (event.changes.status !== undefined) {
            const wasCompleted = node.status === 'completed';
            const isNowCompleted = event.changes.status === 'completed';

            if (wasCompleted !== isNowCompleted) {
              setStreams((prevStreams) =>
                prevStreams.map((s) =>
                  s.id === prev.id
                    ? { ...s, progress: { ...s.progress, completed: s.progress.completed + (isNowCompleted ? 1 : -1) } }
                    : s
                )
              );
            }
          }

          return {
            ...prev,
            nodes: prev.nodes.map((n) => {
              if (n.id !== event.node_id) return n;
              const updated = { ...n };
              if (event.changes.name !== undefined) updated.name = event.changes.name;
              if (event.changes.status !== undefined) updated.status = event.changes.status;
              if (event.changes.x !== undefined) updated.x = event.changes.x;
              if (event.changes.y !== undefined) updated.y = event.changes.y;
              if (event.changes.meta !== undefined) {
                let existingMeta = {};
                if (n.meta) {
                  try { existingMeta = JSON.parse(n.meta); } catch {
                    // Ignore parse errors, use empty object
                  }
                }
                updated.meta = JSON.stringify({ ...existingMeta, ...event.changes.meta });
              }
              updated.updated_at = event.timestamp;
              return updated;
            }),
          };
        });
        break;
      }

      case 'node_deleted': {
        setCurrentStreamData((prev) => {
          if (!prev) return prev;
          const node = prev.nodes.find((n) => n.id === event.node_id);
          if (!node) return prev;

          // Update streams list progress (decrement total, and completed if node was completed)
          setStreams((prevStreams) =>
            prevStreams.map((s) =>
              s.id === prev.id ? {
                ...s,
                progress: {
                  completed: node.status === 'completed' ? s.progress.completed - 1 : s.progress.completed,
                  total: s.progress.total - 1,
                },
              } : s
            )
          );

          return {
            ...prev,
            nodes: prev.nodes.filter((n) => n.id !== event.node_id),
            edges: prev.edges.filter((e) => e.from_node_id !== event.node_id && e.to_node_id !== event.node_id),
          };
        });
        break;
      }

      case 'edge_created': {
        if (event.edge.stream_id === currentStreamData.id) {
          setCurrentStreamData((prev) =>
            prev ? {
              ...prev,
              edges: [...prev.edges, {
                id: event.edge.id,
                stream_id: event.edge.stream_id,
                from_node_id: event.edge.from_node_id,
                to_node_id: event.edge.to_node_id,
                label: event.edge.label,
                created_at: event.timestamp,
              }],
            } : prev
          );
        }
        break;
      }

      case 'edge_deleted': {
        setCurrentStreamData((prev) =>
          prev ? {
            ...prev,
            edges: prev.edges.filter((e) => e.id !== event.edge_id),
          } : prev
        );
        break;
      }

      case 'node_status_changed': {
        // Update node status directly (emitted by agent loops)
        // Also update progress count symmetrically when entering/leaving completed
        setCurrentStreamData((prev) => {
          if (!prev) return prev;
          const node = prev.nodes.find((n) => n.id === event.node_id);
          if (!node) return prev;

          const wasCompleted = node.status === 'completed';
          const isNowCompleted = event.status === 'completed';

          // Update streams list progress symmetrically
          if (wasCompleted !== isNowCompleted) {
            setStreams((prevStreams) =>
              prevStreams.map((s) =>
                s.id === prev.id
                  ? { ...s, progress: { ...s.progress, completed: s.progress.completed + (isNowCompleted ? 1 : -1) } }
                  : s
              )
            );
          }

          return {
            ...prev,
            nodes: prev.nodes.map((n) => {
              if (n.id !== event.node_id) return n;
              return { ...n, status: event.status, updated_at: event.timestamp ?? n.updated_at };
            }),
          };
        });
        break;
      }

      // Agent loop events - these are informational, no state changes needed
      // They can be used by other components (e.g., NodeCard) for real-time activity display
      case 'agent_started':
      case 'agent_iteration':
      case 'agent_complete':
      case 'agent_cancelled':
      case 'content':
      case 'thinking':
      case 'tool_requested':
      case 'tool_executed':
        // These events are handled by individual node components, not the streams context
        break;

      case 'workflow_started':
      case 'workflow_terminated':
      case 'message_received':
      case 'tool_approval_response':
      case 'error':
        // Internal reliability events - no UI state change needed
        // workflow_started/terminated: Used by UserSessionDO for workflow concurrency tracking
        // message_received: Delivery confirmation handled by useWorkflowChat
        // tool_approval_response: Handled by useWorkflowChat
        // error: Handled by useWorkflowChat
        break;

      default: {
        // Exhaustive check - if we get here, we're missing a case
        const _exhaustiveCheck: never = event;
        axiom.warn('[StreamsContext] Unknown event type', { event: _exhaustiveCheck });
      }
    }
  }, [currentStreamData]);

  // Fetch streams on auth change
  useEffect(() => {
    if (user && accessInfo?.hasAccess) {
      fetchStreams();
    } else {
      setStreams([]);
      setSelectedStreamId(null);
      setCurrentStreamData(null);
    }
  }, [user, accessInfo?.hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch stream data when selection changes
  useEffect(() => {
    if (selectedStreamId) {
      fetchStreamData(selectedStreamId).catch((err) => axiom.error('Error caught', { error: err instanceof Error ? err.message : String(err) }));
    }
  }, [selectedStreamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: StreamsContextType = {
    streams,
    loading,
    error,
    selectedStreamId,
    currentStreamData,
    currentStreamLoading,
    fetchStreams,
    retryFetchStreams,
    createStream,
    updateStream,
    deleteStream,
    selectStream,
    fetchStreamData,
    createNode,
    updateNode,
    deleteNode,
    createEdge,
    deleteEdge,
    refreshCurrentStream,
    autoLayoutStream,
    handleWebSocketEvent,
  };

  return <StreamsContext.Provider value={value}>{children}</StreamsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStreams() {
  const context = useContext(StreamsContext);
  if (!context) {
    throw new Error('useStreams must be used within a StreamsProvider');
  }
  return context;
}
